import loadable from "@loadable/component";
import stringify from "fast-json-stable-stringify";
import { isLeft } from "fp-ts/lib/Either";
import { inject, observer } from "mobx-react";
import { useMemo, useState } from "react";
import type { Store } from "@inferno/renderer-shared-core";
import { Remote } from "../../components/remote/Remote.component";
import { ChartBlockResolver } from "../../decoders/Charts.types";
import { getPaths } from "@ihr-radioedit/inferno-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { getChartV2 } from "../../services/Charts";
import { PageBlockInterface } from "../Block.component";

const ChartListsCollection = loadable(() => import("./ChartListsCollection.component"));
const ChartListsCollectionSkeleton = loadable(
  () => import("../../components/skeletons/ChartListsCollectionSkeleton.component"),
);

const log = ILog.logger("Chart Lists Collection Page Loader");

export interface ChartListsCollectionPageLoaderProps extends PageBlockInterface {
  store?: Store;
}

export const ChartListsCollectionPageLoader = inject("store")(
  observer(({ store, block, params, isPrimary }: ChartListsCollectionPageLoaderProps) => {
    if (!block || !store) {
      return null;
    }

    if (!params?.chart_slug) {
      log.error("Chart Lists Collection Block Failed Validation: missing required 'chart_slug' param");
      return null;
    }

    const resolvedBlock = ChartBlockResolver.decode(block.value);

    if (isLeft(resolvedBlock)) {
      log.error(`Chart Lists Collection Block Failed Validation\n ${getPaths(resolvedBlock).join("\n")}`);
      return null;
    }

    const { size, title } = resolvedBlock.right;

    const today = useMemo(() => Date.now(), []);

    const [month, setMonth] = useState<number | null>(null);
    const [year, setYear] = useState<number | null>(null);

    const endDate = useMemo(() => {
      let date = year ? Date.UTC(year, (month !== null ? month : 11) + 1, 0, 23, 59, 59, 999) : null;
      if (date && date > today) {
        date = today;
      }
      return date;
    }, [year, month, today]);

    const startDate = useMemo(
      () => (year ? Date.UTC(year, month !== null ? month : 0, 1, 0, 0, 0, 0) : null),
      [year, month],
    );

    return (
      <Remote
        cacheKey={`chart-lists-collection-v2-${stringify({
          chartSlug: params.chart_slug,
          size,
          startDate,
          endDate,
        })}`}
        fallback={<ChartListsCollectionSkeleton />}
        loader={() =>
          getChartV2({
            slug: params.chart_slug!,
            listsSize: size,
            listsStartDate: startDate,
            listsEndDate: endDate,
            includeDates: true,
            includeLists: true,
          })
        }
        showLoading={isPrimary}
      >
        {({ data: chart }) =>
          chart ? (
            <ChartListsCollection
              isPrimary={isPrimary}
              setMonth={setMonth}
              setYear={setYear}
              month={month}
              title={title}
              block={block}
              chart={chart}
              year={year}
            />
          ) : null
        }
      </Remote>
    );
  }),
);

export default ChartListsCollectionPageLoader;
